import { Button, InlineNotice, Panel } from "@attmos/flowstack-ui";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { resolveTtyEndpoint } from "@/api/client";
import {
  fetchWorkspace,
  useStartWorkspace,
  useWorkspace,
  workspaceQueryKey,
  workspaceTtyUrl,
  type WorkspaceStatus,
} from "@/api/workspace";
import {
  redeemTtyPass,
  TerminalConnection,
  type ConnectionState,
} from "@/components/workspace/terminal-connection";
import { apiErrorMessage } from "@/lib/api-error-kind";
import { RefusalDetail } from "@/routes/projects/RefusalDetail";

/** Lines of history xterm keeps on screen; the agent's 256 KB ring is what survives a reconnect. */
const SCROLLBACK_LINES = 5_000;

/**
 * A project's Terminal (Phase 2 U2.2, fs#2337): a shell in the project's
 * workspace session, over the tty broker. Since fs#2533 it is drawn in the
 * app-wide terminal dock (`components/layout/TerminalDock.tsx`), one instance
 * per open project tab, all mounted at once — so nothing here may assume it is
 * the only terminal on the page.
 *
 * The panel follows the session: no session (or a stopped, failed or
 * suspended one) offers Start/Resume; a `starting` one is polled; a `running`
 * one gets the terminal. The shell lives in the container, not in this tab —
 * closing or reloading the page leaves it running for the detach window, and
 * the next attach replays its scrollback.
 *
 * **One-click start (fs#2537).** A `startRequestId` is a user's click that
 * asked for this project's shell — the rail's Terminal or the Workbench's
 * "Open in dock". The panel acts on each request ONCE, against a FRESH read
 * of the session (a cached status may be minutes old): with no running
 * workspace it presses its own Start (the same `start.mutate()`, so an
 * `idle_suspended` session resumes under its id); with one running, starting
 * or stopping it just attaches. It hands the id back through
 * `onStartRequestHandled` at once. A failed start shows the server's refusal
 * beside the Start button, which is the retry — nothing retries by itself.
 * With no request the panel starts nothing, as before.
 *
 * At most one start per panel is ever in flight: a request that arrives while
 * one is being decided or sent just attaches to it (a double click POSTs
 * once). A panel unmounted mid-decision — the dock closed — starts nothing.
 */
export function TerminalPanel({
  slug,
  startRequestId = null,
  onStartRequestHandled,
}: {
  slug: string;
  startRequestId?: number | null;
  onStartRequestHandled?: (id: number) => void;
}): JSX.Element {
  const workspace = useWorkspace(slug);
  const start = useStartWorkspace(slug);
  // Refs, not state: StrictMode re-runs an effect with the same props, and
  // none of these may let a second run POST again.
  const handled = React.useRef<number | null>(null);
  const busy = React.useRef(false);
  const alive = React.useRef(false);
  // `busy` for the render: the Start button stays disabled while a click's
  // start is being decided, so it cannot add a second POST of its own.
  const [deciding, setDeciding] = React.useState(false);

  React.useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const { refetch } = workspace;
  const { mutate, isPending } = start;
  React.useEffect(() => {
    if (startRequestId === null || handled.current === startRequestId) return;
    handled.current = startRequestId;
    onStartRequestHandled?.(startRequestId);
    if (busy.current || isPending) return;
    busy.current = true;
    setDeciding(true);
    const done = (): void => {
      busy.current = false;
      if (alive.current) setDeciding(false);
    };
    void refetch().then((result) => {
      const state = result.data?.state;
      // A status that did not load gets no blind start: its error shows.
      if (
        !alive.current ||
        result.isError ||
        state === "running" ||
        state === "starting" ||
        state === "stopping"
      ) {
        done();
        return;
      }
      mutate(undefined, { onSettled: done });
    });
  }, [startRequestId, onStartRequestHandled, refetch, mutate, isPending]);

  let body: React.ReactNode;
  if (workspace.isLoading) {
    body = (
      <p role="status" className="text-sm text-muted-foreground">
        Loading the workspace…
      </p>
    );
  } else if (workspace.isError) {
    body = (
      <InlineNotice tone="danger" title="Could not load the workspace">
        {apiErrorMessage(workspace.error)}
      </InlineNotice>
    );
  } else if (workspace.data?.state === "running") {
    body = <TerminalScreen slug={slug} />;
  } else if (workspace.data?.state === "starting" || workspace.data?.state === "stopping") {
    body = (
      <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
        {workspace.data.state === "starting"
          ? "Starting the workspace…"
          : "The workspace is stopping…"}
      </p>
    );
  } else {
    const status = workspace.data ?? null;
    const resumable = status?.state === "idle_suspended";
    body = (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-muted-foreground">{idleText(status)}</p>
        <Button type="button" disabled={start.isPending || deciding} onClick={() => start.mutate()}>
          {resumable ? "Resume the workspace" : "Start a workspace"}
        </Button>
        {start.isError && (
          <InlineNotice tone="danger" title="The workspace did not start">
            <RefusalDetail error={start.error} />
          </InlineNotice>
        )}
      </div>
    );
  }

  return (
    <Panel title="Terminal" headingLevel={2}>
      {body}
    </Panel>
  );
}

function idleText(status: WorkspaceStatus | null): string {
  if (status === null) return "No workspace is running for this project.";
  if (status.state === "idle_suspended") {
    return "The workspace is suspended; its files are kept. Resume it to get a shell.";
  }
  if (status.state === "failed") {
    return `The last workspace failed${status.error ? `: ${status.error}` : "."}`;
  }
  return "The last workspace has stopped.";
}

function TerminalScreen({ slug }: { slug: string }): JSX.Element {
  const queryClient = useQueryClient();
  const host = React.useRef<HTMLDivElement>(null);
  const connection = React.useRef<TerminalConnection | null>(null);
  const [state, setState] = React.useState<ConnectionState>({ kind: "connecting" });

  React.useEffect(() => {
    let disposed = false;
    let teardown = (): void => {};
    void import("@/components/workspace/xterm").then(({ FitAddon, Terminal }) => {
      const element = host.current;
      if (disposed || element === null) return;
      const terminal = new Terminal({ scrollback: SCROLLBACK_LINES, cursorBlink: true });
      const fit = new FitAddon();
      terminal.loadAddon(fit);
      terminal.open(element);
      fit.fit();

      const conn = new TerminalConnection({
        url: () => workspaceTtyUrl(slug),
        // A separate terminal host needs a fresh pass before every dial (C2.9).
        ttyEndpoint: resolveTtyEndpoint,
        redeemPass: (ttyEndpoint) => redeemTtyPass(slug, ttyEndpoint),
        // A fresh USER token per dial: an established socket outlives its
        // token, a reconnect does not.
        token: async () => {
          const status = await queryClient.fetchQuery({
            queryKey: workspaceQueryKey(slug),
            queryFn: () => fetchWorkspace(slug),
            staleTime: 0,
          });
          return status?.state === "running" ? status.token : null;
        },
        // The agent replays its ring on every attach: start from a clean screen.
        onOpen: () => terminal.reset(),
        onData: (bytes) => terminal.write(bytes),
        onState: (next) => {
          setState(next);
          if (next.kind === "ended") {
            void queryClient.invalidateQueries({ queryKey: workspaceQueryKey(slug) });
          }
        },
      });
      const encoder = new TextEncoder();
      const typed = terminal.onData((text) => conn.write(encoder.encode(text)));
      const binary = terminal.onBinary((text) =>
        conn.write(Uint8Array.from(text, (char) => char.charCodeAt(0) & 0xff))
      );
      const resized = terminal.onResize(({ cols, rows }) => conn.resize(cols, rows));
      conn.resize(terminal.cols, terminal.rows);
      const observer = new ResizeObserver(() => fit.fit());
      observer.observe(element);
      connection.current = conn;
      conn.connect();

      teardown = () => {
        observer.disconnect();
        typed.dispose();
        binary.dispose();
        resized.dispose();
        conn.dispose();
        terminal.dispose();
      };
    });
    return () => {
      disposed = true;
      connection.current = null;
      teardown();
    };
  }, [slug, queryClient]);

  return (
    <div className="flex flex-col gap-2">
      <ConnectionNotice state={state} onReconnect={() => connection.current?.connect()} />
      <div
        ref={host}
        data-testid="workspace-terminal"
        className="h-80 w-full overflow-hidden rounded-md bg-black p-1"
      />
    </div>
  );
}

function ConnectionNotice({
  state,
  onReconnect,
}: {
  state: ConnectionState;
  onReconnect: () => void;
}): JSX.Element | null {
  switch (state.kind) {
    case "open":
      return null;
    case "connecting":
      return (
        <p role="status" className="text-sm text-muted-foreground">
          Connecting…
        </p>
      );
    case "reconnecting":
      return (
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
          Reconnecting…
        </p>
      );
    case "superseded":
      return (
        <InlineNotice tone="warning" title="This terminal is open in another tab">
          <Button type="button" variant="outline" size="sm" onClick={onReconnect}>
            Use it here
          </Button>
        </InlineNotice>
      );
    case "denied":
      return (
        <InlineNotice tone="danger" title="The terminal could not authenticate">
          <Button type="button" variant="outline" size="sm" onClick={onReconnect}>
            Try again
          </Button>
        </InlineNotice>
      );
    case "ended":
      return <InlineNotice tone="info" title="The workspace session has ended" />;
  }
}
